import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SearchIcon, FilterIcon, SortAscIcon, PackageIcon, SparklesIcon, LoaderIcon } from 'lucide-react';
import Button from '../components/common/Button';
import FlightCard from '../components/search/FlightCard';
import HotelCard from '../components/search/HotelCard';
import PackageCard from '../components/search/PackageCard';
import RecommendedPackagesList from '../components/search/RecommendedPackagesList';
import { searchFlights, searchHotels, searchPackages } from '../utils/mockData';
import { Flight, Hotel, Package } from '../utils/types';
const SearchResults = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchType = searchParams.get('type') || 'flight';
  const flightType = searchParams.get('flightType') || 'roundTrip';
  const from = searchParams.get('from') || '';
  const to = searchParams.get('to') || '';
  const departureDate = searchParams.get('departureDate') || '';
  const returnDate = searchParams.get('returnDate') || '';
  const location = searchParams.get('location') || searchParams.get('hotelLocation') || to || '';
  const checkIn = searchParams.get('checkIn') || departureDate || '';
  const checkOut = searchParams.get('checkOut') || returnDate || '';
  const travelers = searchParams.get('travelers') || '';
  // State for search results
  const [flights, setFlights] = useState<Flight[]>([]);
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);
  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [showRecommendedPackages, setShowRecommendedPackages] = useState(false);
  const [activeTab, setActiveTab] = useState<'flights' | 'hotels' | 'ai-packages'>(searchType === 'flight' ? 'flights' : searchType === 'hotel' ? 'hotels' : 'flights' // Default to flights for package search
  );
  const [isGeneratingAIPackages, setIsGeneratingAIPackages] = useState(false);
  const [aiPackagesGenerated, setAiPackagesGenerated] = useState(false);
  // Fetch search results based on search type
  useEffect(() => {
    setLoading(true);
    // Simulate API call delay
    const timer = setTimeout(() => {
      if (searchType === 'flight') {
        setFlights(searchFlights(from, to));
        setHotels([]);
        setPackages([]);
      } else if (searchType === 'hotel') {
        setHotels(searchHotels(location));
        setFlights([]);
        setPackages([]);
      } else {
        // For package searches, fetch both flights and hotels
        setFlights(searchFlights(from, to));
        setHotels(searchHotels(location || to));
        setPackages([]); // Don't fetch packages yet, wait for AI recommendation
      }
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchType, from, to, location]);
  // Handle generating AI recommendations
  const generateAIPackages = () => {
    if (!aiPackagesGenerated) {
      setIsGeneratingAIPackages(true);
      // Simulate AI processing time
      setTimeout(() => {
        setPackages(searchPackages(from, to));
        setIsGeneratingAIPackages(false);
        setAiPackagesGenerated(true);
      }, 2500); // Simulate 2.5 seconds of AI processing
    }
  };
  // When user clicks on AI packages tab, start generating
  useEffect(() => {
    if (activeTab === 'ai-packages' && !aiPackagesGenerated && !isGeneratingAIPackages) {
      generateAIPackages();
    }
  }, [activeTab]);
  // Determine if we have search results
  const hasResults = activeTab === 'flights' && flights.length > 0 || activeTab === 'hotels' && hotels.length > 0 || activeTab === 'ai-packages' && packages.length > 0 || searchType === 'recommended-packages' && packages.length > 0;
  // Handle selecting a flight for package building
  const handleSelectFlight = (flight: Flight) => {
    setSelectedFlight(flight);
    if (searchType === 'package') {
      setActiveTab('hotels');
    } else {
      navigate(`/booking/select?flightId=${flight.id}`);
    }
  };
  // Handle selecting a hotel for package building
  const handleSelectHotel = (hotel: Hotel) => {
    setSelectedHotel(hotel);
    if (searchType === 'package' && selectedFlight) {
      // If we have both flight and hotel, navigate to booking flow
      navigate(`/booking/prebook?type=package&flightId=${selectedFlight.id}&hotelId=${hotel.id}`);
    } else {
      navigate(`/booking/select?hotelId=${hotel.id}`);
    }
  };
  // Handle direct navigation to the new booking flow for package searches
  const handleStartPackageBooking = () => {
    navigate(`/booking/select?type=package&from=${from}&to=${to}&departureDate=${departureDate}&returnDate=${returnDate}&travelers=${travelers}`);
  };
  // Toggle recommended packages view
  const toggleRecommendedPackages = () => {
    setShowRecommendedPackages(!showRecommendedPackages);
  };
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-997">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-998">
        <div className="bg-white p-6 rounded-lg shadow-md mb-6" data-id="element-999">
          <h1 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1000">
            {searchType === 'flight' ? `Flight Search${flightType === 'oneWay' ? ' (One Way)' : flightType === 'roundTrip' ? ' (Round Trip)' : ' (Multi-City)'}` : searchType === 'hotel' ? 'Hotel Search' : searchType === 'recommended-packages' ? 'Recommended Packages' : 'Flight + Hotel Search'}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600" data-id="element-1001">
            {from && <div className="flex items-center" data-id="element-1002">
                <span className="font-semibold mr-1" data-id="element-1003">From:</span> {from}
              </div>}
            {to && <div className="flex items-center" data-id="element-1004">
                <span className="font-semibold mr-1" data-id="element-1005">To:</span> {to}
              </div>}
            {departureDate && <div className="flex items-center" data-id="element-1006">
                <span className="font-semibold mr-1" data-id="element-1007">Departure:</span>{' '}
                {departureDate}
              </div>}
            {returnDate && <div className="flex items-center" data-id="element-1008">
                <span className="font-semibold mr-1" data-id="element-1009">Return:</span> {returnDate}
              </div>}
            {location && searchType !== 'flight' && <div className="flex items-center" data-id="element-1010">
                <span className="font-semibold mr-1" data-id="element-1011">Location:</span> {location}
              </div>}
            {checkIn && searchType !== 'flight' && <div className="flex items-center" data-id="element-1012">
                <span className="font-semibold mr-1" data-id="element-1013">Check-in:</span> {checkIn}
              </div>}
            {checkOut && searchType !== 'flight' && <div className="flex items-center" data-id="element-1014">
                <span className="font-semibold mr-1" data-id="element-1015">Check-out:</span>{' '}
                {checkOut}
              </div>}
            {travelers && <div className="flex items-center" data-id="element-1016">
                <span className="font-semibold mr-1" data-id="element-1017">Travelers:</span>{' '}
                {travelers}
              </div>}
          </div>
          {searchType === 'package' && <div className="mt-4" data-id="element-1018">
              {/* Package search tabs */}
              <div className="border-b border-gray-200" data-id="element-1019">
                <nav className="flex -mb-px space-x-8" data-id="element-1020">
                  <button onClick={() => setActiveTab('flights')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'flights' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1021">
                    Flights
                  </button>
                  <button onClick={() => setActiveTab('hotels')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'hotels' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1022">
                    Hotels
                  </button>
                  <button onClick={() => setActiveTab('ai-packages')} className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === 'ai-packages' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1023">
                    <SparklesIcon className="h-4 w-4 mr-1" data-id="element-1024" />
                    AI Recommended Packages
                  </button>
                </nav>
              </div>
              {/* Package building instructions */}
              {activeTab === 'flights' && <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-1025">
                  <h2 className="text-lg font-medium text-blue-800 mb-2" data-id="element-1026">
                    Step 1: Select Your Flight
                  </h2>
                  <p className="text-blue-700 mb-3" data-id="element-1027">
                    Choose your preferred flight first, then you'll be able to
                    select a hotel to complete your package.
                  </p>
                  {selectedFlight && <div className="bg-white p-3 rounded-md border border-blue-200 mb-3" data-id="element-1028">
                      <p className="font-medium text-blue-800" data-id="element-1029">
                        Selected Flight:
                      </p>
                      <p className="text-sm text-blue-700" data-id="element-1030">
                        {selectedFlight.airline} - {selectedFlight.flightNumber}{' '}
                        from {selectedFlight.departureCity} to{' '}
                        {selectedFlight.arrivalCity}
                      </p>
                      <button onClick={() => setSelectedFlight(null)} className="text-sm text-blue-600 hover:text-blue-800 mt-2" data-id="element-1031">
                        Change Selection
                      </button>
                    </div>}
                  <Button variant="secondary" onClick={toggleRecommendedPackages} data-id="element-1032">
                    {showRecommendedPackages ? 'Build Your Own Package' : 'Show Recommended Packages'}
                  </Button>
                </div>}
              {activeTab === 'hotels' && <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-1033">
                  <h2 className="text-lg font-medium text-blue-800 mb-2" data-id="element-1034">
                    {selectedFlight ? 'Step 2: Select Your Hotel' : 'Select a Flight First'}
                  </h2>
                  <p className="text-blue-700 mb-3" data-id="element-1035">
                    {selectedFlight ? 'Now choose a hotel to complete your custom package.' : 'Please go back to the Flights tab and select a flight before choosing a hotel.'}
                  </p>
                  {selectedFlight && <div className="bg-white p-3 rounded-md border border-blue-200 mb-3" data-id="element-1036">
                      <p className="font-medium text-blue-800" data-id="element-1037">
                        Selected Flight:
                      </p>
                      <p className="text-sm text-blue-700" data-id="element-1038">
                        {selectedFlight.airline} - {selectedFlight.flightNumber}{' '}
                        from {selectedFlight.departureCity} to{' '}
                        {selectedFlight.arrivalCity}
                      </p>
                      <button onClick={() => {
                setSelectedFlight(null);
                setActiveTab('flights');
              }} className="text-sm text-blue-600 hover:text-blue-800 mt-2" data-id="element-1039">
                        Change Selection
                      </button>
                    </div>}
                </div>}
              {activeTab === 'ai-packages' && <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-1040">
                  <div className="flex items-start" data-id="element-1041">
                    <SparklesIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5" data-id="element-1042" />
                    <div data-id="element-1043">
                      <h2 className="text-lg font-medium text-blue-800 mb-2" data-id="element-1044">
                        AI Recommended Flight + Hotel Packages
                      </h2>
                      <p className="text-blue-700 mb-3" data-id="element-1045">
                        Our AI analyzes thousands of flight and hotel
                        combinations to find the best value packages for your
                        trip.
                      </p>
                    </div>
                  </div>
                  {isGeneratingAIPackages && <div className="flex flex-col items-center py-6" data-id="element-1046">
                      <div className="animate-spin mb-3" data-id="element-1047">
                        <LoaderIcon className="h-8 w-8 text-blue-600" data-id="element-1048" />
                      </div>
                      <p className="text-blue-700 font-medium" data-id="element-1049">
                        Our AI is analyzing the perfect packages for you...
                      </p>
                      <p className="text-blue-600 text-sm mt-1" data-id="element-1050">
                        Finding the best combinations of flights and hotels
                      </p>
                    </div>}
                  {!isGeneratingAIPackages && <div className="flex space-x-4" data-id="element-1051">
                      <Button variant="primary" onClick={() => setActiveTab('flights')} data-id="element-1052">
                        Build Your Own Package
                      </Button>
                      {!aiPackagesGenerated && <Button variant="secondary" onClick={generateAIPackages} data-id="element-1053">
                          Generate Recommendations
                        </Button>}
                    </div>}
                </div>}
            </div>}
        </div>
        {/* Show recommended packages if toggled */}
        {searchType === 'package' && showRecommendedPackages && <div className="mb-6" data-id="element-1054">
            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1055">
              <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1056">
                Recommended Packages
              </h2>
              <p className="text-gray-600 mb-6" data-id="element-1057">
                These pre-selected packages offer the best value for your trip.
              </p>
              <div className="space-y-6" data-id="element-1058">
                {packages.map(pkg => <PackageCard key={pkg.id} package={pkg} data-id="element-1059" />)}
              </div>
              <div className="mt-6 border-t border-gray-200 pt-6 text-center" data-id="element-1060">
                <Button variant="secondary" onClick={toggleRecommendedPackages} data-id="element-1061">
                  Return to Search Results
                </Button>
              </div>
            </div>
          </div>}
        {searchType === 'recommended-packages' ?
      // Recommended packages view
      loading ?
      // Loading state
      <div className="bg-white p-8 rounded-lg shadow-md text-center" data-id="element-1062">
              <div className="animate-pulse flex flex-col items-center" data-id="element-1063">
                <div className="rounded-full bg-gray-200 h-12 w-12 mb-4" data-id="element-1064"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" data-id="element-1065"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2" data-id="element-1066"></div>
              </div>
            </div> : <RecommendedPackagesList packages={packages} data-id="element-1067" /> :
      // Regular search results view
      <div className="flex flex-col md:flex-row gap-6" data-id="element-1068">
            {/* Filters sidebar */}
            <div className="md:w-1/4" data-id="element-1069">
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1070">
                <div className="flex items-center justify-between mb-4" data-id="element-1071">
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-1072">
                    Filters
                  </h2>
                  <FilterIcon className="h-5 w-5 text-gray-500" data-id="element-1073" />
                </div>
                <div className="border-t border-gray-200 pt-4" data-id="element-1074">
                  <h3 className="font-medium text-gray-900 mb-2" data-id="element-1075">
                    Price Range
                  </h3>
                  <div className="flex items-center" data-id="element-1076">
                    <input type="range" min="0" max="2000" className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" data-id="element-1077" />
                  </div>
                  <div className="flex justify-between mt-2 text-sm text-gray-600" data-id="element-1078">
                    <span data-id="element-1079">$0</span>
                    <span data-id="element-1080">$2,000+</span>
                  </div>
                </div>
                {searchType !== 'hotel' && activeTab !== 'hotels' && <div className="border-t border-gray-200 pt-4 mt-4" data-id="element-1081">
                    <h3 className="font-medium text-gray-900 mb-2" data-id="element-1082">Airlines</h3>
                    <div className="space-y-2" data-id="element-1083">
                      <div className="flex items-center" data-id="element-1084">
                        <input id="airline-1" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1085" />
                        <label htmlFor="airline-1" className="ml-2 text-sm text-gray-700" data-id="element-1086">
                          American Airlines
                        </label>
                      </div>
                      <div className="flex items-center" data-id="element-1087">
                        <input id="airline-2" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1088" />
                        <label htmlFor="airline-2" className="ml-2 text-sm text-gray-700" data-id="element-1089">
                          Delta Airlines
                        </label>
                      </div>
                      <div className="flex items-center" data-id="element-1090">
                        <input id="airline-3" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1091" />
                        <label htmlFor="airline-3" className="ml-2 text-sm text-gray-700" data-id="element-1092">
                          United Airlines
                        </label>
                      </div>
                    </div>
                  </div>}
                {searchType !== 'flight' && activeTab !== 'flights' && <div className="border-t border-gray-200 pt-4 mt-4" data-id="element-1093">
                    <h3 className="font-medium text-gray-900 mb-2" data-id="element-1094">
                      Hotel Rating
                    </h3>
                    <div className="space-y-2" data-id="element-1095">
                      <div className="flex items-center" data-id="element-1096">
                        <input id="rating-5" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1097" />
                        <label htmlFor="rating-5" className="ml-2 text-sm text-gray-700" data-id="element-1098">
                          5 Stars
                        </label>
                      </div>
                      <div className="flex items-center" data-id="element-1099">
                        <input id="rating-4" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1100" />
                        <label htmlFor="rating-4" className="ml-2 text-sm text-gray-700" data-id="element-1101">
                          4 Stars
                        </label>
                      </div>
                      <div className="flex items-center" data-id="element-1102">
                        <input id="rating-3" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1103" />
                        <label htmlFor="rating-3" className="ml-2 text-sm text-gray-700" data-id="element-1104">
                          3 Stars
                        </label>
                      </div>
                    </div>
                  </div>}
                <div className="border-t border-gray-200 pt-4 mt-4" data-id="element-1105">
                  <h3 className="font-medium text-gray-900 mb-2" data-id="element-1106">
                    More Filters
                  </h3>
                  <div className="space-y-2" data-id="element-1107">
                    {searchType !== 'flight' && activeTab !== 'flights' && <>
                        <div className="flex items-center" data-id="element-1108">
                          <input id="filter-refundable" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1109" />
                          <label htmlFor="filter-refundable" className="ml-2 text-sm text-gray-700" data-id="element-1110">
                            Refundable
                          </label>
                        </div>
                        <div className="flex items-center" data-id="element-1111">
                          <input id="filter-breakfast" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1112" />
                          <label htmlFor="filter-breakfast" className="ml-2 text-sm text-gray-700" data-id="element-1113">
                            Breakfast Included
                          </label>
                        </div>
                      </>}
                    {searchType !== 'hotel' && activeTab !== 'hotels' && <>
                        <div className="flex items-center" data-id="element-1114">
                          <input id="filter-nonstop" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1115" />
                          <label htmlFor="filter-nonstop" className="ml-2 text-sm text-gray-700" data-id="element-1116">
                            Non-stop Flights
                          </label>
                        </div>
                      </>}
                  </div>
                </div>
                <div className="mt-6" data-id="element-1117">
                  <Button variant="primary" fullWidth data-id="element-1118">
                    Apply Filters
                  </Button>
                </div>
              </div>
            </div>
            {/* Search results */}
            <div className="md:w-3/4" data-id="element-1119">
              <div className="bg-white p-6 rounded-lg shadow-md mb-6" data-id="element-1120">
                <div className="flex justify-between items-center" data-id="element-1121">
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-1122">
                    {searchType === 'package' ? activeTab === 'flights' ? 'Available Flights' : activeTab === 'hotels' ? 'Available Hotels' : 'AI Recommended Packages' : 'Search Results'}
                  </h2>
                  <div className="flex items-center" data-id="element-1123">
                    <SortAscIcon className="h-5 w-5 text-gray-500 mr-2" data-id="element-1124" />
                    <select className="text-sm border-gray-300 rounded-md" data-id="element-1125">
                      <option data-id="element-1126">Price: Low to High</option>
                      <option data-id="element-1127">Price: High to Low</option>
                      {searchType !== 'flight' && activeTab !== 'flights' && <option data-id="element-1128">Rating: High to Low</option>}
                      {searchType !== 'hotel' && activeTab !== 'hotels' && <option data-id="element-1129">Duration: Short to Long</option>}
                    </select>
                  </div>
                </div>
              </div>
              {loading || activeTab === 'ai-packages' && isGeneratingAIPackages ?
          // Loading state
          <div className="bg-white p-8 rounded-lg shadow-md text-center" data-id="element-1130">
                  <div className="animate-pulse flex flex-col items-center" data-id="element-1131">
                    <div className="rounded-full bg-gray-200 h-12 w-12 mb-4" data-id="element-1132"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" data-id="element-1133"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2" data-id="element-1134"></div>
                  </div>
                </div> : hasResults || activeTab === 'ai-packages' && aiPackagesGenerated ?
          // Results list
          <div className="space-y-6" data-id="element-1135">
                  {(searchType === 'flight' || searchType === 'package' && activeTab === 'flights') && flights.map(flight => <div key={flight.id} onClick={() => handleSelectFlight(flight)} className={`cursor-pointer ${selectedFlight?.id === flight.id ? 'ring-2 ring-blue-500' : ''}`} data-id="element-1136">
                        <FlightCard flight={flight} data-id="element-1137" />
                      </div>)}
                  {(searchType === 'hotel' || searchType === 'package' && activeTab === 'hotels') && hotels.map(hotel => <div key={hotel.id} onClick={() => handleSelectHotel(hotel)} className={`cursor-pointer ${selectedHotel?.id === hotel.id ? 'ring-2 ring-blue-500' : ''}`} data-id="element-1138">
                        <HotelCard hotel={hotel} data-id="element-1139" />
                      </div>)}
                  {searchType === 'package' && activeTab === 'ai-packages' && aiPackagesGenerated && packages.map(pkg => <PackageCard key={pkg.id} package={pkg} data-id="element-1140" />)}
                </div> :
          // No results state or AI packages not generated yet
          <div className="bg-white p-8 rounded-lg shadow-md text-center" data-id="element-1141">
                  {activeTab === 'ai-packages' && !aiPackagesGenerated ? <>
                      <SparklesIcon className="h-12 w-12 text-blue-400 mx-auto" data-id="element-1142" />
                      <h3 className="mt-4 text-lg font-medium text-gray-900" data-id="element-1143">
                        Generate AI Recommendations
                      </h3>
                      <p className="mt-2 text-gray-600 mb-6" data-id="element-1144">
                        Our AI can analyze your preferences and find the perfect
                        flight and hotel combinations.
                      </p>
                      <Button variant="primary" onClick={generateAIPackages} data-id="element-1145">
                        Generate Recommendations
                      </Button>
                    </> : <>
                      <SearchIcon className="h-12 w-12 text-gray-400 mx-auto" data-id="element-1146" />
                      <h3 className="mt-4 text-lg font-medium text-gray-900" data-id="element-1147">
                        No results found
                      </h3>
                      <p className="mt-2 text-gray-600" data-id="element-1148">
                        Try adjusting your search criteria or filters to find
                        what you're looking for.
                      </p>
                      <div className="mt-6" data-id="element-1149">
                        <Button variant="primary" data-id="element-1150">Modify Search</Button>
                      </div>
                    </>}
                </div>}
            </div>
          </div>}
      </div>
    </div>;
};
export default SearchResults;